<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <!-- Status do Sistema -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Status do Sistema FHIR</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    @click="checkSystemStatus" 
                    class="btn btn-outline-primary float-right"
                    :disabled="loadingStatus"
                  >
                    <i class="ri-refresh-line me-1"></i>
                    Atualizar Status
                  </button>
                </div>
              </div>
            </div>

            <div v-if="loadingStatus" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Verificando status...</p>
            </div>

            <div v-else class="row">
              <div class="col-md-3">
                <div class="card bg-soft-primary border-primary">
                  <div class="card-body text-center">
                    <i class="ri-server-line font-size-24 text-primary mb-2"></i>
                    <h6 class="card-title text-primary">Servidor FHIR</h6>
                    <p class="card-text">
                      <span :class="systemStatus.fhir_server ? 'text-success' : 'text-danger'">
                        {{ systemStatus.fhir_server ? 'Online' : 'Offline' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div class="col-md-3">
                <div class="card bg-soft-success border-success">
                  <div class="card-body text-center">
                    <i class="ri-hospital-line font-size-24 text-success mb-2"></i>
                    <h6 class="card-title text-success">e-SUS</h6>
                    <p class="card-text">
                      <span :class="systemStatus.esus_connection ? 'text-success' : 'text-danger'">
                        {{ systemStatus.esus_connection ? 'Conectado' : 'Desconectado' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div class="col-md-3">
                <div class="card bg-soft-info border-info">
                  <div class="card-body text-center">
                    <i class="ri-government-line font-size-24 text-info mb-2"></i>
                    <h6 class="card-title text-info">RNDS</h6>
                    <p class="card-text">
                      <span :class="systemStatus.rnds_connection ? 'text-success' : 'text-danger'">
                        {{ systemStatus.rnds_connection ? 'Conectado' : 'Desconectado' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div class="col-md-3">
                <div class="card bg-soft-warning border-warning">
                  <div class="card-body text-center">
                    <i class="ri-settings-3-line font-size-24 text-warning mb-2"></i>
                    <h6 class="card-title text-warning">Configurações</h6>
                    <p class="card-text">
                      <span :class="systemStatus.configurations_valid ? 'text-success' : 'text-danger'">
                        {{ systemStatus.configurations_valid ? 'Válidas' : 'Inválidas' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Logs de Conversão -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Logs de Conversão FHIR</h4>
                <p class="card-title-desc">
                  Histórico de conversões de exames para formato FHIR
                </p>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    @click="loadConversionLogs" 
                    class="btn btn-outline-secondary me-2 float-right"
                    :disabled="loadingConversionLogs"
                  >
                    <i class="ri-refresh-line me-1"></i>
                    Atualizar
                  </button>
                  <button 
                    @click="showFiltersModal = true" 
                    class="btn btn-outline-primary mr-2 float-right"
                  >
                    <i class="ri-filter-line me-1"></i>
                    Filtros
                  </button>
                </div>
              </div>
            </div>

            <!-- Filtros rápidos -->
            <div class="row mb-3">
              <div class="col-md-3">
                <select v-model="conversionFilters.status" @change="loadConversionLogs" class="form-select">
                  <option value="">Todos os status</option>
                  <option value="success">Sucesso</option>
                  <option value="error">Erro</option>
                  <option value="pending">Pendente</option>
                </select>
              </div>
              <div class="col-md-3">
                <select v-model="conversionFilters.period" @change="loadConversionLogs" class="form-select">
                  <option value="today">Hoje</option>
                  <option value="week">Última semana</option>
                  <option value="month">Último mês</option>
                  <option value="all">Todos</option>
                </select>
              </div>
            </div>

            <div v-if="loadingConversionLogs" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Carregando logs...</p>
            </div>

            <div v-else-if="conversionLogs.length === 0" class="text-center py-5">
              <div class="mb-3">
                <i class="ri-file-list-2-line font-size-48 text-muted"></i>
              </div>
              <h5 class="text-muted">Nenhum log encontrado</h5>
              <p class="text-muted">Ainda não há conversões FHIR registradas</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-centered table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Data/Hora</th>
                    <th>Exame ID</th>
                    <th>Paciente</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Tamanho</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in conversionLogs" :key="log.id">
                    <td>
                      <small>{{ formatDateTime(log.created_at) }}</small>
                    </td>
                    <td>
                      <span class="badge bg-soft-info text-info">{{ log.exam_id }}</span>
                    </td>
                    <td>
                      <h6 class="mb-0">{{ log.patient_name || 'N/A' }}</h6>
                      <small class="text-muted">{{ log.patient_cpf || 'N/A' }}</small>
                    </td>
                    <td>{{ log.conversion_type || 'FHIR' }}</td>
                    <td>
                      <span 
                        :class="getStatusClass(log.status)"
                      >
                        {{ getStatusLabel(log.status) }}
                      </span>
                    </td>
                    <td>
                      <small>{{ formatFileSize(log.fhir_document_size) }}</small>
                    </td>
                    <td>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" 
                                type="button" data-bs-toggle="dropdown" aria-expanded="false">
                          Ações
                        </button>
                        <ul class="dropdown-menu">
                          <li>
                            <a class="dropdown-item" href="#" @click="viewConversionDetails(log)">
                              <i class="ri-eye-line me-2"></i>Ver detalhes
                            </a>
                          </li>
                          <li v-if="log.status === 'success'">
                            <a class="dropdown-item" href="#" @click="downloadFHIRDocument(log)">
                              <i class="ri-download-line me-2"></i>Download FHIR
                            </a>
                          </li>
                          <li v-if="log.status === 'error'">
                            <a class="dropdown-item" href="#" @click="retryConversion(log)">
                              <i class="ri-refresh-line me-2"></i>Tentar novamente
                            </a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Logs de Submissão -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Logs de Submissão</h4>
                <p class="card-title-desc">
                  Histórico de envios para e-SUS e RNDS
                </p>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    @click="loadSubmissionLogs" 
                    class="btn btn-outline-secondary float-right"
                    :disabled="loadingSubmissionLogs"
                  >
                    <i class="ri-refresh-line me-1"></i>
                    Atualizar
                  </button>
                </div>
              </div>
            </div>

            <!-- Filtros rápidos para submissão -->
            <div class="row mb-3">
              <div class="col-md-3">
                <select v-model="submissionFilters.target" @change="loadSubmissionLogs" class="form-select">
                  <option value="">Todos os destinos</option>
                  <option value="esus">e-SUS</option>
                  <option value="rnds">RNDS</option>
                </select>
              </div>
              <div class="col-md-3">
                <select v-model="submissionFilters.status" @change="loadSubmissionLogs" class="form-select">
                  <option value="">Todos os status</option>
                  <option value="success">Sucesso</option>
                  <option value="error">Erro</option>
                  <option value="pending">Pendente</option>
                </select>
              </div>
            </div>

            <div v-if="loadingSubmissionLogs" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Carregando logs...</p>
            </div>

            <div v-else-if="submissionLogs.length === 0" class="text-center py-5">
              <div class="mb-3">
                <i class="ri-send-plane-line font-size-48 text-muted"></i>
              </div>
              <h5 class="text-muted">Nenhum envio encontrado</h5>
              <p class="text-muted">Ainda não há submissões registradas</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-centered table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Data/Hora</th>
                    <th>Destino</th>
                    <th>Exame ID</th>
                    <th>Status</th>
                    <th>Resposta</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in submissionLogs" :key="log.id">
                    <td>
                      <small>{{ formatDateTime(log.submitted_at) }}</small>
                    </td>
                    <td>
                      <span 
                        :class="log.target_system === 'esus' ? 'badge bg-soft-success text-success' : 'badge bg-soft-info text-info'"
                      >
                        {{ log.target_system?.toUpperCase() || 'N/A' }}
                      </span>
                    </td>
                    <td>
                      <span class="badge bg-soft-info text-info">{{ log.conversion_log_id }}</span>
                    </td>
                    <td>
                      <span :class="getStatusClass(log.status)">
                        {{ getStatusLabel(log.status) }}
                      </span>
                    </td>
                    <td>
                      <small class="text-muted">
                        {{ log.response_message ? log.response_message.substring(0, 50) + '...' : 'N/A' }}
                      </small>
                    </td>
                    <td>
                      <button class="btn btn-sm btn-outline-primary" @click="viewSubmissionDetails(log)">
                        <i class="ri-eye-line"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes de Conversão -->
    <b-modal
      v-model="showConversionDetailsModal"
      title="Detalhes da Conversão FHIR"
      size="lg"
    >
      <div v-if="selectedConversionLog">
        <div class="row">
          <div class="col-md-6">
            <h6>Informações Básicas</h6>
            <ul class="list-unstyled">
              <li><strong>ID:</strong> {{ selectedConversionLog.id }}</li>
              <li><strong>Exame ID:</strong> {{ selectedConversionLog.exam_id }}</li>
              <li><strong>Status:</strong> 
                <span :class="getStatusClass(selectedConversionLog.status)">
                  {{ getStatusLabel(selectedConversionLog.status) }}
                </span>
              </li>
              <li><strong>Data:</strong> {{ formatDateTime(selectedConversionLog.created_at) }}</li>
            </ul>
          </div>
          <div class="col-md-6">
            <h6>Resultado</h6>
            <ul class="list-unstyled">
              <li><strong>Tamanho do documento:</strong> {{ formatFileSize(selectedConversionLog.fhir_document_size) }}</li>
              <li><strong>Tempo de processamento:</strong> {{ selectedConversionLog.processing_time_seconds }}s</li>
            </ul>
          </div>
        </div>
        
        <div v-if="selectedConversionLog.error_message" class="mt-3">
          <h6>Mensagem de Erro</h6>
          <div class="alert alert-danger">
            {{ selectedConversionLog.error_message }}
          </div>
        </div>
      </div>

      <template #modal-footer>
        <button type="button" class="btn btn-secondary" @click="showConversionDetailsModal = false">
          Fechar
        </button>
        <button v-if="selectedConversionLog && selectedConversionLog.status === 'success'" 
                type="button" class="btn btn-primary" @click="downloadFHIRDocument(selectedConversionLog)">
          <i class="ri-download-line me-1"></i>
          Download FHIR
        </button>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin } from "@/mixins/authMixin";
import { fhirSystemService, fhirLogsService } from "@/api/fhir";

export default {
  page: {
    title: "Logs FHIR",
    meta: [{ name: "description", content: "Logs e monitoramento FHIR" }],
  },
  components: { Layout, PageHeader },
  mixins: [authMixin],
  data() {
    return {
      title: "Logs FHIR",
      items: [
        { text: "Integração", href: "/integration" },
        { text: "Logs FHIR", active: true },
      ],
      
      // Estados de carregamento
      loadingStatus: false,
      loadingConversionLogs: false,
      loadingSubmissionLogs: false,
      
      // Dados
      systemStatus: {
        fhir_server: false,
        esus_connection: false,
        rnds_connection: false,
        configurations_valid: false
      },
      conversionLogs: [],
      submissionLogs: [],
      
      // Filtros
      conversionFilters: {
        status: '',
        period: 'week'
      },
      submissionFilters: {
        target: '',
        status: ''
      },
      
      // Modais
      showFiltersModal: false,
      showConversionDetailsModal: false,
      selectedConversionLog: null
    };
  },
  
  async mounted() {
    await this.loadData();
  },
  
  methods: {
    async loadData() {
      await Promise.all([
        this.checkSystemStatus(),
        this.loadConversionLogs(),
        this.loadSubmissionLogs()
      ]);
    },
    
    async checkSystemStatus() {
      this.loadingStatus = true;
      try {
        const response = await fhirSystemService.getStatus();
        this.systemStatus = response.data || {};
      } catch (error) {
        console.error('Erro ao verificar status do sistema:', error);
        this.$toast.error('Erro ao verificar status do sistema');
      } finally {
        this.loadingStatus = false;
      }
    },
    
    async loadConversionLogs() {
      this.loadingConversionLogs = true;
      try {
        const params = {};
        if (this.conversionFilters.status) {
          params.status = this.conversionFilters.status;
        }
        if (this.conversionFilters.period !== 'all') {
          params.period = this.conversionFilters.period;
        }
        
        const response = await fhirLogsService.getConversionLogs(params);
        this.conversionLogs = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar logs de conversão:', error);
        this.$toast.error('Erro ao carregar logs de conversão');
      } finally {
        this.loadingConversionLogs = false;
      }
    },
    
    async loadSubmissionLogs() {
      this.loadingSubmissionLogs = true;
      try {
        const params = {};
        if (this.submissionFilters.target) {
          params.target = this.submissionFilters.target;
        }
        if (this.submissionFilters.status) {
          params.status = this.submissionFilters.status;
        }
        
        const response = await fhirLogsService.getSubmissionLogs(params);
        this.submissionLogs = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar logs de submissão:', error);
        this.$toast.error('Erro ao carregar logs de submissão');
      } finally {
        this.loadingSubmissionLogs = false;
      }
    },
    
    viewConversionDetails(log) {
      this.selectedConversionLog = log;
      this.showConversionDetailsModal = true;
    },
    
    viewSubmissionDetails(log) {
      // Implementar modal para detalhes de submissão
      console.log('Ver detalhes da submissão:', log);
    },
    
    async downloadFHIRDocument(log) {
      try {
        // Implementar download do documento FHIR
        console.log('Download FHIR:', log);
        this.$toast.info('Funcionalidade de download em desenvolvimento');
      } catch (error) {
        console.error('Erro ao fazer download:', error);
        this.$toast.error('Erro ao fazer download do documento');
      }
    },
    
    async retryConversion(log) {
      try {
        // Implementar retry da conversão
        console.log('Retry conversão:', log);
        this.$toast.info('Funcionalidade de retry em desenvolvimento');
      } catch (error) {
        console.error('Erro ao tentar novamente:', error);
        this.$toast.error('Erro ao tentar conversão novamente');
      }
    },
    
    // Utilitários
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    },
    
    formatFileSize(bytes) {
      if (!bytes) return 'N/A';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    },
    
    getStatusClass(status) {
      const classes = {
        'success': 'badge bg-soft-success text-success',
        'error': 'badge bg-soft-danger text-danger',
        'pending': 'badge bg-soft-warning text-warning',
        'processing': 'badge bg-soft-info text-info'
      };
      return classes[status] || 'badge bg-soft-secondary text-secondary';
    },
    
    getStatusLabel(status) {
      const labels = {
        'success': 'Sucesso',
        'error': 'Erro',
        'pending': 'Pendente',
        'processing': 'Processando'
      };
      return labels[status] || status;
    }
  }
};
</script>

<style scoped>
.font-size-24 {
  font-size: 24px;
}

.font-size-48 {
  font-size: 48px;
}
</style>
